import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { useNavigate } from 'react-router-dom';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SOCSettingsData {
  sla_minutes: string;
  storm_threshold: string;
  critical_cid_codes: string;
  high_cid_codes: string;
  medium_cid_codes: string;
  notification_emails: string;
}

interface SuppressionRule {
  id: string;
  site: string;
  location: string;
  alarmType: string;
  reason: string;
  durationMinutes: number;
  expiresAt: number;
  createdBy: string;
  createdAt: string;
  active: boolean;
}

interface AckTemplate {
  id: number;
  organisationId: number;
  phrase: string;
  category: string;
  usageCount: number;
  isDefault: boolean;
  createdAt: string;
}

interface SiteZone {
  id: number;
  siteId: number;
  zoneNumber: number;
  zoneLabel: string;
  area: string;
  zoneType: string;
  severity: string;
  isActive: boolean;
}

interface Site {
  id: number;
  accountCode: string;
  siteName: string;
  address?: string;
  suburb?: string;
}

// ─── Tab type ─────────────────────────────────────────────────────────────────
type Tab = 'sla' | 'severity' | 'storm' | 'emails' | 'suppression' | 'templates' | 'zones';

const TABS: Array<{ id: Tab; label: string; icon: string }> = [
  { id: 'sla',         label: 'SLA',            icon: '⏱' },
  { id: 'severity',    label: 'Severity',        icon: '🎯' },
  { id: 'storm',       label: 'Storm Grouping',  icon: '⛈' },
  { id: 'emails',      label: 'Notifications',   icon: '📧' },
  { id: 'suppression', label: 'Suppression',     icon: '🔕' },
  { id: 'templates',   label: 'Ack Templates',   icon: '📝' },
  { id: 'zones',       label: 'Zone Names',      icon: '📍' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('lemai_token') || ''}`, 'Content-Type': 'application/json' };
}

function fmtExpiry(epochSec: number) {
  if (!epochSec) return '—';
  const d = new Date(epochSec * 1000);
  return d.toLocaleString('en-AU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: '#0d0d0d', border: '1px solid #1a1a1a', borderRadius: '5px', padding: '20px', marginBottom: '16px' }}>
      <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#e5e5e5', marginBottom: '14px', marginTop: 0 }}>{title}</h3>
      {children}
    </div>
  );
}

function FieldLabel({ children, hint }: { children: React.ReactNode; hint?: string }) {
  return (
    <div style={{ marginBottom: '6px' }}>
      <label style={{ fontSize: '12px', fontWeight: 600, color: '#a0a0a0' }}>{children}</label>
      {hint && <span style={{ fontSize: '11px', color: '#555555', marginLeft: '8px' }}>{hint}</span>}
    </div>
  );
}

function SaveBtn({ loading, label = 'Save' }: { loading: boolean; label?: string }) {
  return (
    <button type="submit" disabled={loading}
      style={{ marginTop: '14px', padding: '8px 20px', background: loading ? '#1e3a5f' : '#2563eb', color: 'white', border: 'none', borderRadius: '4px', fontSize: '13px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer' }}>
      {loading ? 'Saving…' : label}
    </button>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function SOCSettings() {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<Tab>('sla');
  const [settings, setSettings] = useState<SOCSettingsData>({
    sla_minutes: '5',
    storm_threshold: '5',
    critical_cid_codes: '120,121,122,130,131,132',
    high_cid_codes: '110,111,112',
    medium_cid_codes: '100,101,102',
    notification_emails: '',
  });
  const [loadingSettings, setLoadingSettings] = useState(true);

  // Suppression
  const [suppressions, setSuppressions] = useState<SuppressionRule[]>([]);
  const [loadingSup, setLoadingSup] = useState(false);
  const [newSup, setNewSup] = useState({ site: '', location: '', alarmType: '', reason: '', durationMinutes: '60' });
  const [addingSup, setAddingSup] = useState(false);
  const [showAddSup, setShowAddSup] = useState(false);

  // Ack Templates
  const [templates, setTemplates] = useState<AckTemplate[]>([]);
  const [loadingTpl, setLoadingTpl] = useState(false);
  const [newTpl, setNewTpl] = useState({ phrase: '', category: 'General' });
  const [addingTpl, setAddingTpl] = useState(false);
  const [showAddTpl, setShowAddTpl] = useState(false);

  // Zones
  const [_sites, setSites] = useState<Site[]>([]);
  const [zones, setZones] = useState<SiteZone[]>([]);
  const [loadingZones, setLoadingZones] = useState(false);
  const [zoneSearch, setZoneSearch] = useState('');
  const [editingZone, setEditingZone] = useState<{ id: number; label: string } | null>(null);
  const [savingZone, setSavingZone] = useState(false);

  // Save states
  const [savingSLA, setSavingSLA] = useState(false);
  const [savingSev, setSavingSev] = useState(false);
  const [savingStorm, setSavingStorm] = useState(false);
  const [savingEmails, setSavingEmails] = useState(false);

  // Access check — only admin / super_admin
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || (user as any)?.is_super_admin;
  useEffect(() => {
    if (user && !isAdmin) {
      navigate('/dashboard');
    }
  }, [user, isAdmin, navigate]);

  // ── Load settings ──────────────────────────────────────────────────────────
  useEffect(() => {
    setLoadingSettings(true);
    fetch('/api/soc-settings', { headers: authHeaders() })
      .then(r => r.json())
      .then(d => {
        if (d.settings) setSettings(s => ({ ...s, ...d.settings }));
      })
      .catch(() => {})
      .finally(() => setLoadingSettings(false));
  }, []);

  // ── Load suppression rules ──────────────────────────────────────────────────
  const loadSuppressions = useCallback(() => {
    setLoadingSup(true);
    fetch('/api/armorip/suppression', { headers: authHeaders() })
      .then(r => r.json())
      .then(d => setSuppressions(d.rules || []))
      .catch(() => {})
      .finally(() => setLoadingSup(false));
  }, []);

  // ── Load ack templates ──────────────────────────────────────────────────────
  const loadTemplates = useCallback(() => {
    setLoadingTpl(true);
    fetch('/api/acknowledgement-templates?limit=50', { headers: authHeaders() })
      .then(r => r.json())
      .then(d => setTemplates(d.templates || []))
      .catch(() => {})
      .finally(() => setLoadingTpl(false));
  }, []);

  // ── Load zones ──────────────────────────────────────────────────────────────
  const loadZones = useCallback(() => {
    setLoadingZones(true);
    Promise.all([
      fetch('/api/sites?include_zones=true', { headers: authHeaders() }).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch('/api/armorip/zones', { headers: authHeaders() }).then(r => r.ok ? r.json() : null).catch(() => null),
    ]).then(([sitesRes, zonesRes]) => {
      const siteList: Site[] = sitesRes?.sites || sitesRes || [];
      setSites(siteList);
      // Collect zones from sites response
      const allZones: SiteZone[] = [];
      if (sitesRes?.sites) {
        for (const site of siteList) {
          if ((site as any).zones) allZones.push(...(site as any).zones);
        }
      }
      if (zonesRes?.zones) allZones.push(...zonesRes.zones);
      setZones(allZones);
    }).finally(() => setLoadingZones(false));
  }, []);

  // Load data for active tab
  useEffect(() => {
    if (activeTab === 'suppression') loadSuppressions();
    if (activeTab === 'templates')   loadTemplates();
    if (activeTab === 'zones')       loadZones();
  }, [activeTab, loadSuppressions, loadTemplates, loadZones]);

  // ── Save handlers ──────────────────────────────────────────────────────────

  async function saveSetting(keys: Partial<SOCSettingsData>, setSaving: (v: boolean) => void) {
    setSaving(true);
    try {
      const r = await fetch('/api/soc-settings', { method: 'PUT', headers: authHeaders(), body: JSON.stringify(keys) });
      const d = await r.json();
      if (d.ok || r.status === 207) {
        setSettings(s => ({ ...s, ...keys }));
        toast('Settings saved', 'success');
      } else {
        toast(d.error || 'Save failed', 'error');
      }
    } catch {
      toast('Network error', 'error');
    } finally {
      setSaving(false);
    }
  }

  // ── SLA Tab ────────────────────────────────────────────────────────────────
  function SLATab() {
    const [sla, setSla] = useState(settings.sla_minutes);
    return (
      <SectionCard title="SLA Response Time">
        <p style={{ fontSize: '12px', color: '#555555', marginBottom: '14px' }}>
          Alarms of Critical or High severity that remain unacknowledged beyond this time trigger an SLA breach notification.
        </p>
        <form onSubmit={e => { e.preventDefault(); saveSetting({ sla_minutes: sla }, setSavingSLA); }}>
          <FieldLabel hint="minutes">SLA Target</FieldLabel>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input type="number" min="1" max="120" value={sla} onChange={e => setSla(e.target.value)}
              style={{ width: '90px', padding: '8px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '7px', color: 'white', fontSize: '14px', fontWeight: 600 }} />
            <span style={{ fontSize: '13px', color: '#555555' }}>minutes</span>
          </div>
          <SaveBtn loading={savingSLA} />
        </form>
      </SectionCard>
    );
  }

  // ── Severity Tab ───────────────────────────────────────────────────────────
  function SeverityTab() {
    const [crit, setCrit]     = useState(settings.critical_cid_codes);
    const [high, setHigh]     = useState(settings.high_cid_codes);
    const [medium, setMedium] = useState(settings.medium_cid_codes);

    return (
      <>
        <div style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: '4px', padding: '12px 14px', marginBottom: '14px', fontSize: '12px', color: '#93c5fd' }}>
          Enter comma-separated CID event codes for each severity level. e.g. 120, 121, 130. Alarms not matching any list default to <strong>Warning</strong>.
        </div>
        <form onSubmit={e => {
          e.preventDefault();
          saveSetting({ critical_cid_codes: crit, high_cid_codes: high, medium_cid_codes: medium }, setSavingSev);
        }}>
          <SectionCard title="Critical (🔴)">
            <FieldLabel hint="e.g. 120, 121, 130">CID Codes</FieldLabel>
            <input value={crit} onChange={e => setCrit(e.target.value)} placeholder="120,121,122,130,131,132"
              style={{ width: '100%', padding: '8px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '7px', color: 'white', fontSize: '13px', boxSizing: 'border-box' }} />
          </SectionCard>
          <SectionCard title="High (🟠)">
            <FieldLabel hint="e.g. 110, 111">CID Codes</FieldLabel>
            <input value={high} onChange={e => setHigh(e.target.value)} placeholder="110,111,112"
              style={{ width: '100%', padding: '8px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '7px', color: 'white', fontSize: '13px', boxSizing: 'border-box' }} />
          </SectionCard>
          <SectionCard title="Medium (🟡)">
            <FieldLabel hint="e.g. 100, 101">CID Codes</FieldLabel>
            <input value={medium} onChange={e => setMedium(e.target.value)} placeholder="100,101,102"
              style={{ width: '100%', padding: '8px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: '7px', color: 'white', fontSize: '13px', boxSizing: 'border-box' }} />
          </SectionCard>
          <SaveBtn loading={savingSev} label="Save Severity Rules" />
        </form>
      </>
    );
  }

  // ── Storm Tab ──────────────────────────────────────────────────────────────
  function StormTab() {
    const [threshold, setThreshold] = useState(settings.storm_threshold);
    return (
      <SectionCard title="Storm Grouping Threshold">
        <p style={{ fontSize: '12px', color: '#555555', marginBottom: '14px' }}>
          When X or more alarms arrive from the same site within 60 seconds, they are grouped into a single storm event.
        </p>
        <form onSubmit={e => { e.preventDefault(); saveSetting({ storm_threshold: threshold }, setSavingStorm); }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '13px', color: '#a0a0a0' }}>Group into storm when</span>
            <input type="number" min="2" max="100" value={threshold} onChange={e => setThreshold(e.target.value)}
              style={{ width: '70px', padding: '8px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '7px', color: 'white', fontSize: '14px', fontWeight: 600, textAlign: 'center' }} />
            <span style={{ fontSize: '13px', color: '#a0a0a0' }}>or more alarms arrive from the same site within 60 seconds.</span>
          </div>
          <SaveBtn loading={savingStorm} />
        </form>
      </SectionCard>
    );
  }

  // ── Emails Tab ─────────────────────────────────────────────────────────────
  function EmailsTab() {
    const [emails, setEmails] = useState(settings.notification_emails);
    return (
      <SectionCard title="SLA Breach & Critical Alarm Notifications">
        <p style={{ fontSize: '12px', color: '#555555', marginBottom: '14px' }}>
          These addresses receive email alerts when an SLA breach occurs or a Critical alarm is raised. Enter one or more addresses separated by commas.
        </p>
        <form onSubmit={e => { e.preventDefault(); saveSetting({ notification_emails: emails }, setSavingEmails); }}>
          <FieldLabel hint="comma-separated">Email Addresses</FieldLabel>
          <textarea value={emails} onChange={e => setEmails(e.target.value)} rows={4}
            placeholder="ops@council.nsw.gov.au, security@council.nsw.gov.au"
            style={{ width: '100%', padding: '8px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '7px', color: 'white', fontSize: '13px', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
          <SaveBtn loading={savingEmails} label="Save Email List" />
        </form>
      </SectionCard>
    );
  }

  // ── Suppression Tab ────────────────────────────────────────────────────────
  function SuppressionTab() {
    async function deleteSup(id: string) {
      if (!window.confirm('Delete this suppression rule?')) return;
      try {
        const r = await fetch(`/api/armorip/suppression/${id}`, { method: 'DELETE', headers: authHeaders() });
        const d = await r.json();
        if (d.ok) { toast('Suppression rule deleted', 'success'); loadSuppressions(); }
        else toast(d.error || 'Delete failed', 'error');
      } catch { toast('Network error', 'error'); }
    }

    async function addSup(e: React.FormEvent) {
      e.preventDefault();
      setAddingSup(true);
      try {
        const r = await fetch('/api/armorip/suppression', { method: 'POST', headers: authHeaders(), body: JSON.stringify({ ...newSup, durationMinutes: parseInt(newSup.durationMinutes) || 60 }) });
        const d = await r.json();
        if (d.ok) {
          toast('Suppression rule added', 'success');
          setNewSup({ site: '', location: '', alarmType: '', reason: '', durationMinutes: '60' });
          setShowAddSup(false);
          loadSuppressions();
        } else toast(d.error || 'Add failed', 'error');
      } catch { toast('Network error', 'error'); } finally { setAddingSup(false); }
    }

    const thStyle: React.CSSProperties = { padding: '8px 10px', textAlign: 'left', fontSize: '10px', fontWeight: 600, color: '#555555', letterSpacing: '0.8px', borderBottom: '1px solid #1a1a1a', whiteSpace: 'nowrap' };
    const tdStyle: React.CSSProperties = { padding: '8px 10px', fontSize: '12px', color: '#cbd5e1', borderBottom: '1px solid rgba(255,255,255,0.04)', verticalAlign: 'top' };

    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <span style={{ fontSize: '13px', color: '#555555' }}>{suppressions.length} rule{suppressions.length !== 1 ? 's' : ''}</span>
          <button onClick={() => setShowAddSup(s => !s)}
            style={{ padding: '7px 14px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
            + Add Rule
          </button>
        </div>

        {showAddSup && (
          <SectionCard title="Add Suppression Rule">
            <form onSubmit={addSup} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {[
                { key: 'site', label: 'Site', placeholder: 'e.g. Town Hall (blank = all sites)' },
                { key: 'location', label: 'Zone/Location', placeholder: 'e.g. Level 2 (blank = all zones)' },
                { key: 'alarmType', label: 'Alarm Type', placeholder: 'e.g. Motion Detected (blank = all types)' },
                { key: 'reason', label: 'Reason', placeholder: 'e.g. Scheduled maintenance', required: true },
              ].map(f => (
                <div key={f.key}>
                  <FieldLabel>{f.label}</FieldLabel>
                  <input value={(newSup as any)[f.key]} onChange={e => setNewSup(s => ({ ...s, [f.key]: e.target.value }))}
                    placeholder={f.placeholder} required={f.required}
                    style={{ width: '100%', padding: '7px 9px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', color: 'white', fontSize: '12px', boxSizing: 'border-box' }} />
                </div>
              ))}
              <div>
                <FieldLabel hint="minutes">Duration</FieldLabel>
                <input type="number" min="1" max="1440" value={newSup.durationMinutes} onChange={e => setNewSup(s => ({ ...s, durationMinutes: e.target.value }))} required
                  style={{ width: '100%', padding: '7px 9px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', color: 'white', fontSize: '12px', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                <button type="submit" disabled={addingSup}
                  style={{ padding: '7px 16px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                  {addingSup ? 'Adding…' : 'Add'}
                </button>
                <button type="button" onClick={() => setShowAddSup(false)}
                  style={{ padding: '7px 12px', background: 'rgba(255,255,255,0.06)', color: '#a0a0a0', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </form>
          </SectionCard>
        )}

        {loadingSup ? (
          <div style={{ padding: '30px', textAlign: 'center', color: '#555555' }}>Loading…</div>
        ) : suppressions.length === 0 ? (
          <div style={{ padding: '30px', textAlign: 'center', color: '#555555', fontSize: '13px' }}>No suppression rules active.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr>
                  {['Site', 'Zone/Location', 'Alarm Type', 'Reason', 'Duration', 'Expires', 'Status', ''].map(h => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {suppressions.map(r => (
                  <tr key={r.id} style={{ background: r.active ? 'rgba(245,158,11,0.04)' : 'transparent' }}>
                    <td style={tdStyle}>{r.site || <span style={{ color: '#555555' }}>All</span>}</td>
                    <td style={tdStyle}>{r.location || <span style={{ color: '#555555' }}>All</span>}</td>
                    <td style={tdStyle}>{r.alarmType || <span style={{ color: '#555555' }}>All</span>}</td>
                    <td style={{ ...tdStyle, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.reason}</td>
                    <td style={tdStyle}>{r.durationMinutes} min</td>
                    <td style={tdStyle}>{fmtExpiry(r.expiresAt)}</td>
                    <td style={tdStyle}>
                      <span style={{ padding: '2px 7px', borderRadius: '6px', fontSize: '10px', fontWeight: 700, background: r.active ? 'rgba(245,158,11,0.2)' : 'rgba(100,116,139,0.2)', color: r.active ? '#fbbf24' : '#555555' }}>
                        {r.active ? 'Active' : 'Expired'}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <button onClick={() => deleteSup(r.id)}
                        style={{ padding: '4px 10px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '5px', color: '#f87171', fontSize: '11px', cursor: 'pointer' }}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  }

  // ── Ack Templates Tab ──────────────────────────────────────────────────────
  function TemplatesTab() {
    const [editId, setEditId] = useState<number | null>(null);
    const [editPhrase, setEditPhrase] = useState('');
    const [editCategory, setEditCategory] = useState('');
    const [savingEdit, setSavingEdit] = useState(false);
    const [deletingId, setDeletingId] = useState<number | null>(null);

    async function deleteTemplate(id: number) {
      if (!window.confirm('Delete this template?')) return;
      setDeletingId(id);
      try {
        const r = await fetch(`/api/acknowledgement-templates/${id}`, { method: 'DELETE', headers: authHeaders() });
        if (r.ok || r.status === 204) {
          toast('Template deleted', 'success');
          setTemplates(ts => ts.filter(t => t.id !== id));
        } else {
          const d = await r.json().catch(() => ({}));
          toast(d.error || 'Delete failed', 'error');
        }
      } catch { toast('Network error', 'error'); } finally { setDeletingId(null); }
    }

    async function addTemplate(e: React.FormEvent) {
      e.preventDefault();
      setAddingTpl(true);
      try {
        const r = await fetch('/api/acknowledgement-templates', {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({ phrase: newTpl.phrase.trim(), category: newTpl.category || 'General' })
        });
        const d = await r.json();
        if (d.ok || d.template) {
          toast('Template added', 'success');
          setNewTpl({ phrase: '', category: 'General' });
          setShowAddTpl(false);
          loadTemplates();
        } else toast(d.error || 'Add failed', 'error');
      } catch { toast('Network error', 'error'); } finally { setAddingTpl(false); }
    }

    const thStyle: React.CSSProperties = { padding: '8px 10px', textAlign: 'left', fontSize: '10px', fontWeight: 600, color: '#555555', letterSpacing: '0.8px', borderBottom: '1px solid #1a1a1a' };
    const tdStyle: React.CSSProperties = { padding: '8px 10px', fontSize: '12px', color: '#cbd5e1', borderBottom: '1px solid rgba(255,255,255,0.04)', verticalAlign: 'middle' };

    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <span style={{ fontSize: '13px', color: '#555555' }}>{templates.length} template{templates.length !== 1 ? 's' : ''}</span>
          <button onClick={() => setShowAddTpl(s => !s)}
            style={{ padding: '7px 14px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
            + Add Template
          </button>
        </div>

        {showAddTpl && (
          <SectionCard title="Add Acknowledgement Template">
            <form onSubmit={addTemplate}>
              <FieldLabel>Phrase</FieldLabel>
              <textarea value={newTpl.phrase} onChange={e => setNewTpl(s => ({ ...s, phrase: e.target.value }))} rows={2} required
                placeholder="e.g. Responded and investigated — no threat found"
                style={{ width: '100%', padding: '8px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '7px', color: 'white', fontSize: '13px', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', marginBottom: '10px' }} />
              <FieldLabel>Category</FieldLabel>
              <input value={newTpl.category} onChange={e => setNewTpl(s => ({ ...s, category: e.target.value }))} placeholder="General"
                style={{ width: '180px', padding: '7px 9px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', color: 'white', fontSize: '12px', marginBottom: '10px' }} />
              <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                <button type="submit" disabled={addingTpl}
                  style={{ padding: '7px 16px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                  {addingTpl ? 'Adding…' : 'Add'}
                </button>
                <button type="button" onClick={() => setShowAddTpl(false)}
                  style={{ padding: '7px 12px', background: 'rgba(255,255,255,0.06)', color: '#a0a0a0', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </form>
          </SectionCard>
        )}

        {loadingTpl ? (
          <div style={{ padding: '30px', textAlign: 'center', color: '#555555' }}>Loading…</div>
        ) : templates.length === 0 ? (
          <div style={{ padding: '30px', textAlign: 'center', color: '#555555', fontSize: '13px' }}>No templates yet.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Phrase', 'Category', 'Uses', 'Default', ''].map(h => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {templates.map(t => (
                  <tr key={t.id}>
                    <td style={{ ...tdStyle, maxWidth: '320px' }}>
                      {editId === t.id ? (
                        <textarea value={editPhrase} onChange={e => setEditPhrase(e.target.value)} rows={2}
                          style={{ width: '100%', padding: '6px 8px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(59,130,246,0.4)', borderRadius: '5px', color: 'white', fontSize: '12px', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                      ) : (
                        <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{t.phrase}</span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      {editId === t.id ? (
                        <input value={editCategory} onChange={e => setEditCategory(e.target.value)}
                          style={{ width: '120px', padding: '5px 8px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(59,130,246,0.4)', borderRadius: '5px', color: 'white', fontSize: '12px' }} />
                      ) : (
                        <span style={{ padding: '2px 7px', borderRadius: '5px', background: 'rgba(59,130,246,0.1)', color: '#93c5fd', fontSize: '10px', fontWeight: 600 }}>{t.category || 'General'}</span>
                      )}
                    </td>
                    <td style={tdStyle}>{t.usageCount || 0}</td>
                    <td style={tdStyle}>{t.isDefault ? <span style={{ color: '#22c55e', fontSize: '10px', fontWeight: 700 }}>DEFAULT</span> : '—'}</td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                      {editId === t.id ? (
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button disabled={savingEdit} onClick={async () => {
                            setSavingEdit(true);
                            try {
                              // PATCH or PUT - use the existing increment endpoint pattern
                              // Lemai doesn't have a PATCH for full edit, so we delete and recreate
                              const delR = await fetch(`/api/acknowledgement-templates/${t.id}`, { method: 'DELETE', headers: authHeaders() });
                              if (delR.ok || delR.status === 204) {
                                const addR = await fetch('/api/acknowledgement-templates', { method: 'POST', headers: authHeaders(), body: JSON.stringify({ phrase: editPhrase, category: editCategory }) });
                                const d = await addR.json();
                                if (d.ok || d.template) { toast('Template updated', 'success'); loadTemplates(); setEditId(null); }
                                else toast(d.error || 'Update failed', 'error');
                              } else toast('Update failed', 'error');
                            } catch { toast('Network error', 'error'); } finally { setSavingEdit(false); }
                          }}
                            style={{ padding: '4px 10px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '5px', fontSize: '11px', cursor: 'pointer' }}>
                            {savingEdit ? '…' : 'Save'}
                          </button>
                          <button onClick={() => setEditId(null)}
                            style={{ padding: '4px 8px', background: 'rgba(255,255,255,0.06)', color: '#a0a0a0', border: 'none', borderRadius: '5px', fontSize: '11px', cursor: 'pointer' }}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button onClick={() => { setEditId(t.id); setEditPhrase(t.phrase); setEditCategory(t.category || 'General'); }}
                            style={{ padding: '4px 10px', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: '5px', color: '#93c5fd', fontSize: '11px', cursor: 'pointer' }}>
                            Edit
                          </button>
                          <button disabled={deletingId === t.id} onClick={() => deleteTemplate(t.id)}
                            style={{ padding: '4px 10px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '5px', color: '#f87171', fontSize: '11px', cursor: 'pointer' }}>
                            {deletingId === t.id ? '…' : 'Delete'}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  }

  // ── Zones Tab ──────────────────────────────────────────────────────────────
  function ZonesTab() {
    const filtered = zones.filter(z =>
      !zoneSearch ||
      (z.zoneLabel || '').toLowerCase().includes(zoneSearch.toLowerCase()) ||
      (z.area || '').toLowerCase().includes(zoneSearch.toLowerCase())
    );

    async function saveZoneLabel(id: number, label: string) {
      setSavingZone(true);
      try {
        const r = await fetch(`/api/sites/zones/${id}`, {
          method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ zone_label: label })
        });
        if (r.ok) {
          setZones(prev => prev.map(z => z.id === id ? { ...z, zoneLabel: label } : z));
          toast('Zone name updated', 'success');
          setEditingZone(null);
        } else {
          const d = await r.json().catch(() => ({}));
          toast(d.error || 'Update failed', 'error');
        }
      } catch { toast('Network error', 'error'); } finally { setSavingZone(false); }
    }

    const thStyle: React.CSSProperties = { padding: '8px 10px', textAlign: 'left', fontSize: '10px', fontWeight: 600, color: '#555555', letterSpacing: '0.8px', borderBottom: '1px solid #1a1a1a', whiteSpace: 'nowrap' };
    const tdStyle: React.CSSProperties = { padding: '8px 10px', fontSize: '12px', color: '#cbd5e1', borderBottom: '1px solid rgba(255,255,255,0.04)', verticalAlign: 'middle' };

    return (
      <>
        <div style={{ marginBottom: '12px' }}>
          <input value={zoneSearch} onChange={e => setZoneSearch(e.target.value)} placeholder="Search zones by name or area…"
            style={{ width: '280px', padding: '8px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '7px', color: 'white', fontSize: '13px' }} />
          <span style={{ marginLeft: '10px', fontSize: '12px', color: '#555555' }}>{filtered.length} zone{filtered.length !== 1 ? 's' : ''}</span>
        </div>

        {loadingZones ? (
          <div style={{ padding: '30px', textAlign: 'center', color: '#555555' }}>Loading zones…</div>
        ) : zones.length === 0 ? (
          <div style={{ padding: '30px', textAlign: 'center', color: '#555555', fontSize: '13px' }}>
            No site zones found. Add zones in Site Programming to manage display names here.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Zone #', 'Display Name', 'Area', 'Type', 'Severity', ''].map(h => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 200).map(z => (
                  <tr key={z.id}>
                    <td style={{ ...tdStyle, color: '#555555', fontFamily: 'monospace' }}>{z.zoneNumber}</td>
                    <td style={tdStyle}>
                      {editingZone?.id === z.id ? (
                        <input autoFocus value={editingZone.label} onChange={e => setEditingZone({ id: z.id, label: e.target.value })}
                          onKeyDown={e => { if (e.key === 'Enter') saveZoneLabel(z.id, editingZone!.label); if (e.key === 'Escape') setEditingZone(null); }}
                          style={{ padding: '5px 8px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(59,130,246,0.4)', borderRadius: '5px', color: 'white', fontSize: '12px', width: '200px' }} />
                      ) : (
                        <span>{z.zoneLabel || <span style={{ color: '#555555' }}>—</span>}</span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, color: '#a0a0a0' }}>{z.area || '—'}</td>
                    <td style={tdStyle}>{z.zoneType || 'other'}</td>
                    <td style={tdStyle}>
                      <span style={{ padding: '2px 6px', borderRadius: '5px', fontSize: '10px', fontWeight: 600, background: z.severity === 'critical' ? 'rgba(239,68,68,0.15)' : z.severity === 'high' ? 'rgba(245,158,11,0.15)' : z.severity === 'medium' ? 'rgba(59,130,246,0.15)' : 'rgba(34,197,94,0.1)', color: z.severity === 'critical' ? '#f87171' : z.severity === 'high' ? '#fbbf24' : z.severity === 'medium' ? '#93c5fd' : '#4ade80' }}>
                        {z.severity || 'medium'}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      {editingZone?.id === z.id ? (
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button disabled={savingZone} onClick={() => saveZoneLabel(z.id, editingZone!.label)}
                            style={{ padding: '4px 10px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '5px', fontSize: '11px', cursor: 'pointer' }}>
                            {savingZone ? '…' : 'Save'}
                          </button>
                          <button onClick={() => setEditingZone(null)}
                            style={{ padding: '4px 8px', background: 'rgba(255,255,255,0.06)', color: '#a0a0a0', border: 'none', borderRadius: '5px', fontSize: '11px', cursor: 'pointer' }}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => setEditingZone({ id: z.id, label: z.zoneLabel || '' })}
                          style={{ padding: '4px 10px', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: '5px', color: '#93c5fd', fontSize: '11px', cursor: 'pointer' }}>
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  if (!user) return null;
  if (!isAdmin) return null;

  return (
    <Layout title="SOC Settings" subtitle="Configure alarm monitoring, SLA, severity classification, suppression, and notification settings">
      {loadingSettings ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#555555' }}>Loading settings…</div>
      ) : (
        <div style={{ maxWidth: '900px' }}>
          {/* Tab bar */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', background: '#0d0d0d', padding: '4px', borderRadius: '4px', border: '1px solid #1a1a1a', flexWrap: 'wrap' }}>
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                style={{ padding: '7px 14px', border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', background: activeTab === tab.id ? 'rgba(59,130,246,0.2)' : 'transparent', color: activeTab === tab.id ? '#93c5fd' : '#555555', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="lm-tab-content">
            {activeTab === 'sla'         && <SLATab />}
            {activeTab === 'severity'    && <SeverityTab />}
            {activeTab === 'storm'       && <StormTab />}
            {activeTab === 'emails'      && <EmailsTab />}
            {activeTab === 'suppression' && <SuppressionTab />}
            {activeTab === 'templates'   && <TemplatesTab />}
            {activeTab === 'zones'       && <ZonesTab />}
          </div>
        </div>
      )}
    </Layout>
  );
}
